/* eslint complexity: 0 */

import React, { PureComponent } from 'react';
import { isString } from 'lodash';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import styles from './button.less';

const FONT_AWESOME_ANIMATIONS = ['spin', 'pulse'];

const buttonFactory = (FontIcon, Link) => {
  class Button extends PureComponent {
    static propTypes = {
      active: PropTypes.bool,
      autoFocus: PropTypes.bool,
      dataTestId: PropTypes.string,
      align: PropTypes.oneOf(['left', 'center', 'right']),
      children: PropTypes.node,
      className: PropTypes.string,
      danger: PropTypes.bool,
      dark: PropTypes.bool,
      disabled: PropTypes.bool,
      flex: PropTypes.bool,
      full: PropTypes.bool,
      gutter: PropTypes.oneOfType([
        PropTypes.oneOf(['left', 'right']),
        PropTypes.bool,
      ]),
      href: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      iconAnimation: PropTypes.oneOfType([
        PropTypes.oneOf(FONT_AWESOME_ANIMATIONS),
        PropTypes.shape({
          ...FONT_AWESOME_ANIMATIONS.reduce(
            (acc, value) => ({ ...acc, [value]: PropTypes.bool }),
            {}
          ),
        }),
      ]),
      iconClassName: PropTypes.string,
      label: PropTypes.string,
      primary: PropTypes.bool,
      reverse: PropTypes.bool,
      shape: PropTypes.oneOf(['circular']),
      size: PropTypes.oneOf(['xs', 'sm', 'm', 'lg', 'xl']),
      theme: PropTypes.oneOf(['raised', 'flat', 'empty']),
      to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      type: PropTypes.string,
    };

    static defaultProps = {
      active: false,
      align: 'center',
      className: '',
      danger: false,
      dark: false,
      disabled: false,
      flex: false,
      full: false,
      gutter: false,
      iconAnimation: {},
      iconClassName: '',
      primary: false,
      reverse: false,
      size: 'm',
      theme: 'raised',
      type: 'button',
    };

    /**
     * Helper function to wrap children passed to button in a span element with
     * the correct className applied will preserving the original props, key and ref
     * of the child.
     *
     * @param {Mix} children - the react children to process
     * @returns {Array} - the transformed children
     **/
    transformChildren = children => {
      return React.Children.map(children, child => {
        // If the child is simply a text node, wrap in a span with the appropriate classname
        if (isString(child)) {
          return <span className={styles['button-text']}>{child}</span>;
        }

        return React.cloneElement(child);
      });
    };

    render() {
      const {
        active,
        align,
        children,
        className,
        danger,
        dark,
        disabled,
        flex,
        full,
        gutter,
        href,
        icon,
        iconAnimation,
        iconClassName,
        label,
        primary,
        reverse,
        shape,
        size,
        theme,
        to,
        type,
        dataTestId,
        ...otherProps
      } = this.props;

      const element = href ? 'a' : to ? Link : 'button'; // eslint-disable-line no-nested-ternary
      const level = primary // eslint-disable-line no-nested-ternary
        ? 'primary'
        : danger // eslint-disable-line no-nested-ternary
        ? 'danger'
        : dark
        ? 'dark'
        : 'neutral';

      const _className = classnames(
        styles.button,
        {
          [styles['is-active']]: active,
          [styles[`button--${level}`]]: level,
          [styles[`button--${theme}`]]: theme,
          [styles['button--flex']]: flex,
          [styles['button--full']]: full,
          [styles['button-gutter--left']]: gutter === 'left' || gutter === true,
          [styles['button-gutter--right']]:
            gutter === 'right' || gutter === true,
          [styles[`button-shape--${shape}`]]: shape,
          [styles[`button-size--${size}`]]: size,
          [styles[`button-align--${align}`]]: align,
          [styles['button-order--reverse']]: reverse,
        },
        className
      );

      const props = {
        'data-test-id': dataTestId || 'button',
        ...otherProps,
        ...(href && { href }),
        ...(to && { to }),
        className: _className,
        disabled: disabled,
        type: !href || !to ? type : null,
      };

      const iconAnimationProps = isString(iconAnimation)
        ? { [iconAnimation]: true }
        : iconAnimation;

      // eslint-disable-next-line no-nested-ternary
      const iconEl = icon ? (
        isString(icon) ? (
          <FontIcon
            className={classnames(styles['button-icon'], iconClassName)}
            name={icon}
            {...iconAnimationProps}
          />
        ) : (
          React.cloneElement(icon, {
            className: classnames(styles['button-icon'], iconClassName),
            ...iconAnimationProps,
          })
        )
      ) : null;

      return React.createElement(
        element,
        props,
        iconEl,
        label,
        this.transformChildren(children)
      );
    }
  }

  return Button;
};

export default buttonFactory;
export { buttonFactory };
